"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.attachSyntheticRecords = void 0;
const remeda_1 = require("remeda");
const common_1 = require("@finley-technologies/common");
const typeguards_1 = require("../typeguards");
const assetVerification = __importStar(require("./assetVerification"));
const assignedLoanTape = __importStar(require("./assignedLoanTape"));
const borrowingBaseReport = __importStar(require("./borrowingBaseReport"));
const cashBalancesFeedContext = __importStar(require("./cashBalancesFeedContext"));
const cashResponseTape = __importStar(require("./cashResponseTape"));
const covenantLoanTape = __importStar(require("./covenantLoanTape"));
const lenderApprovalFlow = __importStar(require("./lenderApprovalFlow"));
const manualBankAccountReport = __importStar(require("./manualBankAccountReport"));
const masterLoanTape = __importStar(require("./masterLoanTape"));
const repaymentTape = __importStar(require("./repaymentTape"));
const utils = __importStar(require("./utils"));
/**
 * Creates the synthetic nodes that exist between generatedLoanTapes and receivableResponseTape.
 * The synthetic nodes that are created depend upon the loanTapeType.
 *
 * List of changes:
 * addMasterLoanTapeArtifacts:
 * 1) if generatedLoanTape.rawMasterLoanTapeArtifactSlug is configured
 *    a) update rawMasterLoanTapeArtifactSlug lpSlug and cfSlug to be same as generatedLoanTape
 * 2) updates generatedLoanTape => receivableResponseTape section of graph to be:
 *    a) generatedLoanTape => masterLoanTape => receivableResponseTape
 *        - masterLoanTape definition depends on loanTapeType
 *    b) updates masterLoanTape configuration to contain rawMasterLoanTapeArtifactSlug and generatedMasterLoanTapeArtifactSlug
 *
 * addAssignedLoanTapeArtifacts:
 * 1) updates masterLoanTape => receivableResponseTape depending on loanTapeType
 *    a) preassignedLoanTape:
 *        - masterLoanTape => single assignedLoanTape => respective receivableResponseTape
 *    b) preassignedMasterLoanTape:
 *        - masterLoanTape => n assignedLoanTapes => respective receivableResponseTape
 *    c) masterLoanTape:
 *        - masterLoanTape => n assignedLoanTapes => respective receivableResponseTape
 *        - masterLoanTape => receivableResponseTapePreview (manual assignment) | break in pipeline, downstream remains the same
 *
 * manualBankAccountReport:
 * 1) updates generatedCashBalanceFeed => borrowingBaseReport to be:
 *    a) generatedCashBalanceFeed => manualBankAccountReport => borrowingBaseReport
 *
 * addCashBalancesFeedContextArtifacts:
 * 1) updates manualBankAccountReport => borrowingBaseReport to be:
 *    a) manualBankAccountReport (kicks off bank-report lambda, restarts pipeline with cashBalancesFeedContext) => break in pipeline
 *    b) add cashBalancesFeedContext => borrowingBaseReport
 *
 * addCashResponseTapeArtifacts:
 * 1) updates cashBalancesFeedContext => borrowingBaseReport to be:
 *    a) cashBalancesFeedContext => cashResponseTape => borrowingBaseReport
 *    b) receivableResponseTape => cashResponseTape
 *
 * materializeBorrowingBaseReportArtifacts:
 * 1) updates borrowing base report with added configuration:
 *    a) receivableResponseTapeArtifactSlug (string)
 *    b) cashBalancesFeedContextArtifactSlug (string | null)
 *    c) repaymentTapeArtifactSlugs (string [])
 */
const attachSyntheticRecords = (dbGraph) => {
    const { dataTapes } = dbGraph;
    const loanTapeTypeMap = utils.generateLoanTapeTypeMap(dataTapes);
    const masterLoanTapeAdapter = (dbGraphParam) => masterLoanTape.addMasterLoanTapeArtifacts({
        dbGraph: dbGraphParam,
        loanTapeTypeMap,
    });
    const assignedLoanTapeAdapter = (dbGraphParam) => assignedLoanTape.addAssignedLoanTapeArtifacts({
        dbGraph: dbGraphParam,
        loanTapeTypeMap,
    });
    const covenantLoanTapeAdapter = (dbGraphParam) => covenantLoanTape.attachCovenantLoanTapeArtifactToMasterLoanTape({
        dbGraph: dbGraphParam,
        loanTapeTypeMap,
    });
    // pipe has 8 ops limit, so split it up
    let finalDbGraph = (0, remeda_1.pipe)(dbGraph, masterLoanTapeAdapter, assignedLoanTapeAdapter, covenantLoanTapeAdapter, manualBankAccountReport.addManualBankAccountReportArtifacts, cashBalancesFeedContext.addCashBalancesFeedContextArtifacts, cashResponseTape.addCashResponseTapeArtifacts, repaymentTape.addInternalRepaymentTapeArtifacts);
    finalDbGraph = (0, remeda_1.pipe)(finalDbGraph, borrowingBaseReport.materializeBorrowingBaseReportArtifacts, lenderApprovalFlow.addLenderApprovalArtifacts, assetVerification.addAssetVerificationArtifacts);
    for (const finalDataTape of finalDbGraph.dataTapes) {
        (0, common_1.finleyAssert)(typeguards_1.typeguards.isTapeBuilderRecordDB(common_1.utils.stripDbTimeStamps(finalDataTape)), new common_1.error.FinleyError(`[${finalDataTape.id}] is invalid tapeBuilder DB record while attaching synthetic`, {
            metadata: {
                logMeta: {
                    errorTape: JSON.stringify(finalDataTape),
                },
            },
        }));
    }
    return finalDbGraph;
};
exports.attachSyntheticRecords = attachSyntheticRecords;
//# sourceMappingURL=attach.js.map